import React from 'react'
import { Avatar, Badge, FormHelperText, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { CheckCircleRounded } from '@mui/icons-material'
import ConfirmDelete from '../../../../../components/dialogs/ConfirmDelete'
import DndIcon from '../../../../../assets/icons/Dnd.svg'
import { CustomRichTextEditor } from '../../../../../components/customRichTextEditor'
import { ALPHABET } from '../../../../../utils/constants'
import { tone, ui } from '../../../../../utils/uiTokens'

const styles = (theme, snapshot) => {
    return {
        helperText: {
            color: tone.bad.text,
            ml: 1,
        },

        textEditor: {
            width: '100%',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            mr: 1,
            ml: 1,
        },

        contentBox: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            mr: 2,
        },

        avatar: {
            background: ui.card,
            color: ui.ink,
            border: '1px solid',
            borderColor: theme.palette.grey[200],
        },

        checkedAnswer: {
            color: tone.ok.text,
            background: ui.card,
            borderRadius: '50%',
        },

        dndOption: {
            mr: 1,
            padding: '8px',
            paddingTop: '12px',
            borderRadius: '50%',
            background: snapshot.isDragging ? theme.palette.grey[100] : ui.card,
            '&:hover': {
                background: theme.palette.grey[100],
            },
        },

        boxContainer: {
            display: 'flex',
            alignItems: 'start',
            mt: 3,
        },
    }
}

interface ItemProps {
    id?: string | number
    answerId?: string | number
    content: string
    correct: boolean
}

interface Props {
    index: number
    item: ItemProps
    key?: number | string
    innerRef?: any
    snapshot?: any
    draggableProps?: any
    dragHandleProps?: any
    handleDelete?: (id: number | string) => void
    handleBadgeClick?: (index: number) => void
    formik: any
    questionIndex: number
}

const SingleOption: React.FC<Props> = ({
    index,
    item,
    handleDelete,
    handleBadgeClick,
    draggableProps,
    dragHandleProps,
    innerRef,
    snapshot,
    formik,
    questionIndex,
}) => {
    const theme = useTheme()

    return (
        <Box
            ref={innerRef}
            sx={styles(theme, snapshot).boxContainer}
            {...draggableProps}
        >
            <Box
                {...dragHandleProps}
                sx={styles(theme, snapshot).dndOption}
            >
                <img
                    alt={'dnd'}
                    src={DndIcon}
                />
            </Box>

            <Badge
                overlap="circular"
                sx={{ cursor: 'pointer', paddingTop: '6px' }}
                invisible={!item.correct}
                onClick={() => handleBadgeClick(index)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                badgeContent={
                    <CheckCircleRounded
                        sx={styles(theme, snapshot).checkedAnswer}
                    />
                }
            >
                <Avatar sx={styles(theme, snapshot).avatar}>
                    {ALPHABET[index].name}
                </Avatar>
            </Badge>

            <Box sx={styles(theme, snapshot).contentBox}>
                <Box
                    className="CustomEditor"
                    sx={styles(theme, snapshot).textEditor}
                >
                    <CustomRichTextEditor
                        value={item.content}
                        inline={true}
                        editable={true}
                        onEditorChange={(text) => {
                            formik.handleChange({
                                target: {
                                    name: `questions[${questionIndex}].answers[${index}].content`,
                                    value: text,
                                },
                            })
                        }}
                    />
                </Box>

                <FormHelperText sx={styles(theme, snapshot).helperText}>
                    {
                        formik.errors?.questions?.[questionIndex]?.answers?.[
                            index
                        ]?.content
                    }
                </FormHelperText>
            </Box>

            <Box sx={{ paddingTop: '8px' }}>
                <ConfirmDelete
                    confirmAction={() => handleDelete(item.id || item.answerId)}
                />
            </Box>
        </Box>
    )
}

export default SingleOption
